import { inject, Injectable } from '@angular/core';
import { ResourceService } from '../../shared/services/resource.service';
import { ChangeState, Contract, ContractWithPeriod, Page, Period, Review, SignedUrl } from '@monorepo/interfaces';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorService } from '../../shared/services/error.service';
import { handleError, makeRequest } from '../../shared/utils/resource.util';
import { HttpMethod } from '../../shared/enums/http-method.enum';
import { FleetBrowseFilters } from '../../shared/interfaces/fleet-management/fleet-browse-filter.interface';
import { TableSortState } from '../../shared/interfaces/table-sort-state';
import { getFleetFilterParams } from '../utils/fleet-management.util';

@Injectable({ providedIn: 'root' })
export class FleetManagementService extends ResourceService<Contract, Contract, Contract> {
    private apiUrl = environment.apiConfig[1].uri;
    private httpClient = inject(HttpClient);
    private errorService = inject(ErrorService);
    constructor() {
        super('contracts');
    }

    public getAllContracts(
        accountingUnitId: string,
        isSubmitted: boolean,
        startMonth: string,
        endMonth: string,
        page = 0,
        pageSize = 20,
        filter?: FleetBrowseFilters,
        sort?: TableSortState
    ): Observable<Page<ContractWithPeriod>> {
        let params = new HttpParams()
            .set('accountingUnitId', accountingUnitId)
            .set('submitted', isSubmitted)
            .set('startMonth', startMonth)
            .set('endMonth', endMonth)
            .set('page', page)
            .set('pageSize', pageSize);
        if (filter) {
            params = getFleetFilterParams(filter, params);
        }
        if (sort && sort.direction && sort.column) {
            params = params.append('orderBy', sort.column).append('sortOrder', sort.direction.toUpperCase());
        }
        return makeRequest<Page<ContractWithPeriod>>(
            this.httpClient,
            HttpMethod.GET,
            `${this.apiUrl}/fleet/contracts`,
            { params },
            handleError<Page<ContractWithPeriod>>(
                `${HttpMethod.GET} Fleet Management Contracts (paginated)`,
                this.errorService
            )
        );
    }

    public getContractsByPeriodId(
        periodId: string,
        changeState: ChangeState | undefined,
        page = 0,
        pageSize = 20
    ): Observable<Page<Contract>> {
        let params = new HttpParams().set('page', page).set('pageSize', pageSize);
        if (changeState) {
            params = params.set('changeState', changeState);
        }
        return makeRequest<Page<Contract>>(
            this.httpClient,
            HttpMethod.GET,
            `${this.apiUrl}/fleet/periods/${periodId}/contracts`,
            { params },
            handleError<Page<Contract>>(`${HttpMethod.GET} Period with contracts (paginated)`, this.errorService)
        );
    }

    public getSelectedPeriod(
        accountingUnitId: string,
        period: string,
        page = 0,
        pageSize = 20
    ): Observable<Page<Period>> {
        const params = new HttpParams()
            .set('accountingUnitId', accountingUnitId)
            .set('month', period)
            .set('page', page)
            .set('pageSize', pageSize);
        return makeRequest<Page<Period>>(
            this.httpClient,
            HttpMethod.GET,
            `${this.apiUrl}/fleet/periods`,
            { params },
            handleError<Page<Period>>(`${HttpMethod.GET} Fleet Management Period`, this.errorService)
        );
    }

    public submitReview(periodId: string, review: Review): Observable<void> {
        return makeRequest<void>(
            this.httpClient,
            HttpMethod.POST,
            `${this.apiUrl}/fleet/periods/${periodId}/reviews`,
            { body: review },
            handleError<void>(`${HttpMethod.POST} Fleet Management Review`, this.errorService)
        );
    }

    public getContractsByPeriodIssues(
        periodId: string,
        page = 0,
        pageSize = 20,
        issueState?: number
    ): Observable<Page<Contract>> {
        let params = new HttpParams().set('page', page).set('pageSize', pageSize);
        if (issueState !== undefined) {
            params = params.set('issueState', issueState);
        }
        return makeRequest<Page<Contract>>(
            this.httpClient,
            HttpMethod.GET,
            `${this.apiUrl}/fleet/periods/${periodId}/issues/contracts`,
            { params },
            handleError<Page<Contract>>(
                `${HttpMethod.GET} Fleet Management Contracts by Period Issues`,
                this.errorService
            )
        );
    }

    public deleteAllPeriodsForAccountingUnit(accountingUnitId: string): Observable<{ deletedCount: number }> {
        return makeRequest<{ deletedCount: number }>(
            this.httpClient,
            HttpMethod.DELETE,
            `${this.apiUrl}/fleet/periods/accounting-unit/${accountingUnitId}`,
            {},
            handleError<{ deletedCount: number }>(
                `${HttpMethod.DELETE} Fleet Management Delete All Periods`,
                this.errorService
            )
        );
    }

    public movePeriodBackward(periodId: string, newMonth: Date): Observable<Period> {
        return makeRequest<Period>(
            this.httpClient,
            HttpMethod.PATCH,
            `${this.apiUrl}/fleet/periods/${periodId}/move-backward`,
            { body: { newMonth } },
            handleError<Period>(`${HttpMethod.PATCH} Fleet Management Move Period Backward`, this.errorService)
        );
    }

    public confirmFleetUpload(uploadId: string): Observable<{ message: string }> {
        return makeRequest<{ message: string }>(
            this.httpClient,
            HttpMethod.POST,
            `${this.apiUrl}/upload/${uploadId}/confirm`,
            {},
            handleError<{ message: string }>(`${HttpMethod.POST} Confirm Fleet Upload`, this.errorService)
        );
    }

    public cancelFleetUpload(uploadId: string): Observable<{ message: string }> {
        return makeRequest<{ message: string }>(
            this.httpClient,
            HttpMethod.POST,
            `${this.apiUrl}/upload/${uploadId}/cancel`,
            {},
            handleError<{ message: string }>(`${HttpMethod.POST} Cancel Fleet Upload`, this.errorService)
        );
    }

    public downloadAllContracts(
        accountingUnitId: string,
        isSubmitted: boolean,
        startMonth: string,
        endMonth: string,
        filter?: FleetBrowseFilters,
        sort?: TableSortState
    ): Observable<SignedUrl> {
        let params = new HttpParams()
            .set('accountingUnitId', accountingUnitId)
            .set('submitted', isSubmitted)
            .set('startMonth', startMonth)
            .set('endMonth', endMonth)
            .set('page', '0')
            .set('pageSize', '0');
        if (filter) {
            params = getFleetFilterParams(filter, params);
        }
        if (sort && sort.direction && sort.column) {
            params = params.append('orderBy', sort.column).append('sortOrder', sort.direction.toUpperCase());
        }

        return makeRequest<SignedUrl>(
            this.httpClient,
            HttpMethod.GET,
            `${this.apiUrl}/fleet/contracts/export/link`,
            { params },
            handleError<SignedUrl>(`${HttpMethod.GET} Fleet Management Contracts Export`, this.errorService)
        );
    }
}
